import React, { useState } from 'react';

const defaultSettings = {
  gridColsDesktop: 3,
  gridGap: 6,
  enableUpload: true,
  allowedTypes: ['image/jpeg', 'image/png'],
  maxFileSizeMB: 5,
  categories: ['Sitewise', 'Datewise', 'Geo location', 'Events', 'Projects', 'Team', 'Other'],
  showDateOnCard: true,
};

const ChipList = ({ items, onRemove }) => (
  <div className="flex flex-wrap gap-2">
    {items.map((item) => (
      <span key={item} className="inline-flex items-center gap-2 rounded border px-2 py-1">
        {item}
        <button onClick={() => onRemove(item)} className="text-red-600" title="Remove">×</button>
      </span>
    ))}
  </div>
);

const AddInline = ({ placeholder, onAdd }) => (
  <div className="flex items-center gap-2">
    <input
      type="text"
      placeholder={placeholder}
      className="rounded-md border px-3 py-2"
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          const val = e.currentTarget.value.trim();
          if (val) onAdd(val);
          e.currentTarget.value = '';
        }
      }}
    />
    <button
      className="px-3 py-2 rounded-md border hover:bg-gray-50"
      onClick={(e) => {
        const input = e.currentTarget.previousSibling;
        if (input && input.value.trim()) {
          onAdd(input.value.trim());
          input.value = '';
        }
      }}
    >Add</button>
  </div>
);

const GallerySettings = () => {
  const [settings, setSettings] = useState(defaultSettings);
  const [saving, setSaving] = useState(false);

  const update = (key, value) => setSettings((s) => ({ ...s, [key]: value }));
  const addUnique = (listKey, value) => {
    update(listKey, Array.from(new Set([...(settings[listKey] || []), value])));
  };
  const removeItem = (listKey, value) => {
    update(listKey, (settings[listKey] || []).filter((v) => v !== value));
  };

  const onReset = () => setSettings(defaultSettings);

  const onSave = async () => {
    setSaving(true);
    try {
      // Replace with API call to persist settings
      await new Promise((r) => setTimeout(r, 600));
      // eslint-disable-next-line no-console
      console.log('Gallery settings saved:', settings);
      alert('Gallery settings saved');
    } catch (e) {
      alert('Failed to save Gallery settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold">Gallery Settings</h2>

      <div className="rounded-lg border p-4 bg-white space-y-4">
        <h3 className="font-medium text-gray-900">Layout</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <label className="flex flex-col gap-1">
            <span className="text-sm text-gray-600">Grid Columns (Desktop)</span>
            <input type="number" min={1} max={6} value={settings.gridColsDesktop} onChange={(e) => update('gridColsDesktop', Number(e.target.value))} className="rounded-md border px-3 py-2" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-sm text-gray-600">Grid Gap</span>
            <input type="number" min={0} max={10} value={settings.gridGap} onChange={(e) => update('gridGap', Number(e.target.value))} className="rounded-md border px-3 py-2" />
          </label>
          <label className="inline-flex items-center gap-2 mt-6 sm:mt-0">
            <input type="checkbox" checked={settings.showDateOnCard} onChange={(e) => update('showDateOnCard', e.target.checked)} />
            <span className="text-gray-700">Show date on card</span>
          </label>
        </div>
      </div>

      <div className="rounded-lg border p-4 bg-white space-y-4">
        <h3 className="font-medium text-gray-900">Upload</h3>
        <label className="inline-flex items-center gap-2">
          <input type="checkbox" checked={settings.enableUpload} onChange={(e) => update('enableUpload', e.target.checked)} />
          <span className="text-gray-700">Enable image upload</span>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <label className="flex flex-col gap-1">
            <span className="text-sm text-gray-600">Max File Size (MB)</span>
            <input type="number" min={1} value={settings.maxFileSizeMB} onChange={(e) => update('maxFileSizeMB', Number(e.target.value))} className="rounded-md border px-3 py-2" />
          </label>
          <div className="sm:col-span-2">
            <span className="text-sm text-gray-600">Allowed MIME Types</span>
            <ChipList items={settings.allowedTypes} onRemove={(item) => removeItem('allowedTypes', item)} />
            <AddInline placeholder="Add MIME type (e.g., image/webp)" onAdd={(val) => addUnique('allowedTypes', val)} />
          </div>
        </div>
      </div>

      <div className="rounded-lg border p-4 bg-white space-y-4">
        <h3 className="font-medium text-gray-900">Categories</h3>
        <ChipList items={settings.categories} onRemove={(item) => removeItem('categories', item)} />
        <AddInline placeholder="Add category" onAdd={(val) => addUnique('categories', val)} />
      </div>

      <div className="flex items-center gap-3">
        <button onClick={onSave} disabled={saving} className={`px-4 py-2 rounded-md text-white ${saving ? 'bg-primary/70' : 'bg-primary hover:opacity-90'}`}>
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
        <button onClick={onReset} disabled={saving} className="px-4 py-2 rounded-md border hover:bg-gray-50">Reset to Defaults</button>
      </div>
    </div>
  );
};

export default GallerySettings;


